/**
 * Two-step sign-in with an authenticator app (1.0.0-rc.4): setting it up (a QR code and the secret, a confirming
 * code, then ten recovery codes shown once), its state under My account, and the page shown instead of PRISM when
 * the administrator requires it and it is not set up yet. 1.0.0-rc.5: the browsers remembered at the code step
 * ("Trusted devices"), with Remove and Remove all.
 */
import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Download, KeyRound, Loader2, MonitorSmartphone, ShieldCheck, ShieldOff, Trash2 } from 'lucide-react';
import { api, errorMessage } from '../api/client';
import type { Me } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import { copy } from '../lib/clipboard';
import { browserLabel, relative } from '../lib/format';
import { toast } from './Toasts';
import { TableScroll } from './TableScroll';

export interface MfaStatus {
  featureEnabled: boolean; enrolled: boolean; enrolledAt: string | null; required: boolean; coveredBySso: boolean; canDisable: boolean; recoveryCodesLeft: number;
  /** Days a browser can be remembered at the code step (1.0.0-rc.5); 0: off. */
  rememberDays?: number;
}

export interface TrustedDevice {
  id: string; network: string; userAgent: string | null; createdAt: string; lastUsedAt: string | null; expiresAt: string; thisBrowser: boolean;
}

/** My account → Two-step sign-in → Trusted devices (1.0.0-rc.5). */
export function TrustedDevices() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['me-trusted-devices'], queryFn: () => api<{ items: TrustedDevice[] }>('/api/v1/me/mfa/trusted-devices') });
  const [busy, setBusy] = useState(false);
  const items = q.data?.items ?? [];
  async function remove(id: string | null) {
    if (id === null && !window.confirm('Remove all trusted devices? Every browser asks for the code at the next sign-in.')) return;
    setBusy(true);
    try {
      await api(id ? `/api/v1/me/mfa/trusted-devices/${id}` : '/api/v1/me/mfa/trusted-devices', { method: 'DELETE' });
      toast(id ? 'Device removed: it asks for the code at the next sign-in' : 'All trusted devices removed');
      await qc.invalidateQueries({ queryKey: ['me-trusted-devices'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="form" style={{ gap: 8 }} data-testid="trusted-devices">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ flex: 1 }}><MonitorSmartphone size={14} style={{ verticalAlign: -2 }} /> Trusted devices</strong>
        {items.length > 1 && <button className="btn ghost sm danger" disabled={busy} onClick={() => void remove(null)} data-testid="trusted-devices-remove-all"><Trash2 size={13} /> Remove all</button>}
      </div>
      <p className="faint" style={{ margin: 0, fontSize: 12 }}>Browsers where you ticked “Don’t ask for a code on this device”. They skip the code only on the same network, and never the password.</p>
      {items.length ? (
        <TableScroll>
        <table className="table compact">
          <thead><tr><th>Browser and network</th><th>Trusted</th><th>Last used</th><th>Expires</th><th /></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} data-testid="trusted-device">
                <td className="wrap" title={d.userAgent ?? undefined}>{browserLabel(d.userAgent)}{d.thisBrowser && <span className="tag" style={{ marginLeft: 6 }}>This browser</span>}
                  <div className="faint mono" style={{ fontSize: 12 }}>{d.network}</div></td>
                <td className="muted" title={new Date(d.createdAt).toLocaleString()}>{relative(d.createdAt)}</td>
                <td className="muted" title={d.lastUsedAt ? new Date(d.lastUsedAt).toLocaleString() : undefined}>{d.lastUsedAt ? relative(d.lastUsedAt) : 'Not yet'}</td>
                <td className="muted" title={new Date(d.expiresAt).toLocaleString()}>{relative(d.expiresAt)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><button className="btn ghost sm" disabled={busy} onClick={() => void remove(d.id)} data-testid="trusted-device-remove"><Trash2 size={13} /> Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      ) : <p className="muted" style={{ margin: 0 }} data-testid="trusted-devices-none">{q.isLoading ? 'Loading…' : 'None. Every sign-in asks for the code.'}</p>}
    </div>
  );
}

export const useMfaStatus = () => useQuery({ queryKey: ['me-mfa'], queryFn: () => api<MfaStatus>('/api/v1/me/mfa') });

/** The code field: six digits, as authenticator apps show them. */
export function CodeInput({ id, value, onChange, autoFocus = true }: { id: string; value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <input id={id} className="input mono code-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" maxLength={7} placeholder="123 456"
      value={value} onChange={(e) => onChange(e.target.value.replace(/[^\d ]/g, ''))} autoFocus={autoFocus} required />
  );
}

/** The ten recovery codes, once: copy or save them. */
export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const text = codes.join('\n');
  const save = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`PRISM recovery codes\nEach works once, instead of a code from your authenticator app.\n\n${text}\n`], { type: 'text/plain' }));
    a.download = 'prism-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="form" style={{ gap: 12 }} data-testid="mfa-recovery-codes">
      <div className="banner warning"><div className="banner-body"><strong>Keep these recovery codes somewhere safe</strong>
        <span>Each works once instead of a code, for example if you lose your phone. They are shown only now.</span></div></div>
      <div className="recovery-codes mono">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn sm" onClick={() => copy(text)}><Copy size={13} /> Copy</button>
        <button type="button" className="btn sm" onClick={save}><Download size={13} /> Save as a file</button>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn primary sm" onClick={onDone} data-testid="mfa-codes-done"><Check size={13} /> I have saved them</button>
      </div>
    </div>
  );
}

/** Setting up: the QR code and secret, then a code to confirm, then the recovery codes. */
export function MfaEnrol({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [started, setStarted] = useState<{ secret: string; qrCode: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function start() {
    setBusy(true);
    setError(null);
    try {
      setStarted(await api('/api/v1/me/mfa/enrol', { method: 'POST', json: {} }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  async function confirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ recoveryCodes: string[] }>('/api/v1/me/mfa/enrol/confirm', { method: 'POST', json: { code: code.replace(/\s/g, '') } });
      // The state is refreshed only once the codes are saved: refreshing now would replace this view and lose them.
      setCodes(r.recoveryCodes);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  if (codes) return <RecoveryCodes codes={codes} onDone={() => { void qc.invalidateQueries({ queryKey: ['me-mfa'] }); void qc.invalidateQueries({ queryKey: queryKeys.me() }); toast('Two-step sign-in is on'); onDone(); }} />;
  if (!started) {
    return (
      <div className="form" style={{ gap: 12 }}>
        {error && <div className="banner error" role="alert">{error}</div>}
        <p className="muted" style={{ margin: 0 }}>You need an authenticator app on your phone, such as Microsoft Authenticator, Google Authenticator, 1Password or Authy.</p>
        <button className="btn primary" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => void start()} data-testid="mfa-start">
          {busy ? <Loader2 size={14} className="spin" /> : <ShieldCheck size={14} />} Set up two-step sign-in</button>
      </div>
    );
  }
  return (
    <form className="form" style={{ gap: 12 }} onSubmit={confirm} data-testid="mfa-enrol">
      {error && <div className="banner error" role="alert">{error}</div>}
      <div className="mfa-qr">
        <img src={started.qrCode} width={180} height={180} alt="QR code for your authenticator app" data-testid="mfa-qr" />
        <div className="mfa-qr-text">
          <p style={{ margin: 0 }}><b>1.</b> Scan the QR code with your authenticator app.</p>
          <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>Or enter this key in it by hand (time-based, 6 digits):</p>
          <div className="mono mfa-secret" data-testid="mfa-secret">{started.secret.match(/.{1,4}/g)!.join(' ')}</div>
          <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => copy(started.secret)}><Copy size={12} /> Copy key</button>
        </div>
      </div>
      <div className="field"><label htmlFor="mfa-code"><b>2.</b> Enter the code the app shows</label><CodeInput id="mfa-code" value={code} onChange={setCode} /></div>
      <button className="btn primary" style={{ alignSelf: 'flex-start' }} type="submit" disabled={busy || code.replace(/\s/g, '').length !== 6} data-testid="mfa-confirm">
        {busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Turn on</button>
    </form>
  );
}

/** My account → Two-step sign-in. */
export function MfaSection() {
  const qc = useQueryClient();
  const s = useMfaStatus();
  const [enrolling, setEnrolling] = useState(false);
  const [acting, setActing] = useState<'off' | 'codes' | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  if (!s.data) return <div className="skeleton" style={{ height: 80 }} />;
  const m = s.data;
  if (codes) return <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />;
  if (!m.featureEnabled && !m.enrolled) return <p className="muted" data-testid="mfa-off">Two-step sign-in is not turned on for this installation. An administrator can turn it on under Administration → Access.</p>;
  if (enrolling) return <MfaEnrol onDone={() => setEnrolling(false)} />;
  async function act(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (acting === 'off') {
        await api('/api/v1/me/mfa', { method: 'DELETE', json: { code: code.replace(/\s/g, '') } });
        toast('Two-step sign-in is off');
      } else {
        setCodes((await api<{ recoveryCodes: string[] }>('/api/v1/me/mfa/recovery-codes', { method: 'POST', json: { code: code.replace(/\s/g, '') } })).recoveryCodes);
      }
      setActing(null);
      setCode('');
      await qc.invalidateQueries({ queryKey: ['me-mfa'] });
      await qc.invalidateQueries({ queryKey: ['me-trusted-devices'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="form" style={{ gap: 12 }} data-testid="mfa-section">
      {m.enrolled ? (
        <div className="banner info"><div className="banner-body"><strong><ShieldCheck size={14} style={{ verticalAlign: -2 }} /> Two-step sign-in is on</strong>
          <span>After your password PRISM asks for the code from your authenticator app. {m.recoveryCodesLeft} recovery code{m.recoveryCodesLeft === 1 ? '' : 's'} left.</span></div></div>
      ) : (
        <p className="muted" style={{ margin: 0 }}>{m.coveredBySso
          ? 'You signed in through your organisation’s identity provider, whose own sign-in check covers you. You can still add PRISM’s code.'
          : m.required ? 'Your administrator requires two-step sign-in for you.' : 'Protect your account with a code from an authenticator app, asked for after your password.'}</p>
      )}
      {!m.enrolled && <MfaEnrol onDone={() => void qc.invalidateQueries({ queryKey: ['me-mfa'] })} />}
      {m.enrolled && !acting && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn sm" onClick={() => setActing('codes')} data-testid="mfa-new-codes"><KeyRound size={13} /> New recovery codes</button>
          {m.canDisable && <button className="btn ghost sm danger" onClick={() => setActing('off')} data-testid="mfa-disable"><ShieldOff size={13} /> Turn off</button>}
          {!m.canDisable && <span className="faint" style={{ fontSize: 12, alignSelf: 'center' }}>Required by your administrator</span>}
        </div>
      )}
      {m.enrolled && !acting && <TrustedDevices />}
      {acting && (
        <form className="form" style={{ gap: 10 }} onSubmit={act}>
          <div className="field"><label htmlFor="mfa-act-code">{acting === 'off' ? 'A code from your app, to turn it off' : 'A code from your app, for new recovery codes (the old ones stop working)'}</label>
            <CodeInput id="mfa-act-code" value={code} onChange={setCode} /></div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn ghost sm" onClick={() => { setActing(null); setCode(''); }}>Cancel</button>
            <button className={`btn sm ${acting === 'off' ? 'danger' : 'primary'}`} type="submit" disabled={busy || code.replace(/\s/g, '').length !== 6}>{acting === 'off' ? 'Turn off' : 'Make new codes'}</button>
          </div>
        </form>
      )}
    </div>
  );
}

/** Shown instead of PRISM until two-step sign-in is set up, when the administrator requires it. */
export function ForcedMfaEnrolment({ me }: { me: Me }) {
  const qc = useQueryClient();
  async function signOut() {
    await api('/api/v1/auth/logout', { method: 'POST', json: {} }).catch(() => undefined);
    qc.clear();
    window.location.assign('/login');
  }
  return (
    <div className="login">
      <div className="login-art" aria-hidden>
        <div className="brand"><img src="/prism.svg" width={30} height={30} alt="" /> PRISM</div>
        <div className="promise">Find any moment. <span>Make it a story.</span> Deliver it anywhere.</div>
        <div className="strip">{Array.from({ length: 5 }, (_, i) => <div key={i} />)}</div>
      </div>
      <div className="login-form-wrap">
        <div className="login-form" style={{ width: 'min(440px, 100%)' }} data-testid="mfa-required">
          <h1 style={{ margin: 0 }}>Set up two-step sign-in</h1>
          <p className="muted" style={{ margin: 0 }}>Your administrator requires a code from an authenticator app when you sign in, {me.user.displayName}. Set it up to continue.</p>
          <MfaEnrol onDone={() => void qc.invalidateQueries({ queryKey: queryKeys.me() })} />
          <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => void signOut()}>Sign out</button>
        </div>
      </div>
    </div>
  );
}
